'use client';

import { type PropsWithChildren, useMemo, useRef, useState } from 'react';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  NavigationHistoryContext,
  type NavigationHistoryContextType,
} from './NavigationHistoryContext';

type NavigationHistoryProviderProps = NonNullable<PropsWithChildren>;

type NavigationHistory = NavigationHistoryContextType['navigationHistory'];

/**
 * @description Context provider for an internal navigation history store
 */
export function NavigationHistoryProvider({
  children,
}: NavigationHistoryProviderProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Use a ref to store the history array. This prevents re-rendering all consumers on every navigation change.
  const historyRef = useRef<NavigationHistory>([]);

  // Use state only for the boolean flag. This will trigger a re-render only when it changes (once).
  const [hasHistory, setHasHistory] = useState(false);

  const contextValue = useMemo(
    () => ({
      navigationHistory: historyRef.current,
      hasHistory,
    }),
    [hasHistory]
  );

  // update the history when the pathname or search params change
  useEffect(() => {
    // generate the url with search params if they exist
    const url =
      searchParams.size > 0
        ? `${pathname}?${searchParams.toString()}`
        : pathname;

    const previousHistory = historyRef.current;

    // avoid adding duplicate entries if the user reloads or the effect runs twice
    if (previousHistory[previousHistory.length - 1] === url) {
      return;
    }

    // append the new url to the history
    historyRef.current = [...previousHistory, url];

    // only update the state to trigger a re-render if the condition changes from false to true
    if (!hasHistory && historyRef.current.length > 1) {
      setHasHistory(true);
    }
  }, [pathname, searchParams, hasHistory]);

  return (
    <NavigationHistoryContext.Provider value={contextValue}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}
